<template>
  <div class="product-info">
    <div class="content info-content">
      <v-row class="pa-5 ma-0">
        <v-col class="flex-grow-0 pa-0 ma-0">
          <div class="image-upload-ctn pr-5">
            <div class="d-flex flex-row align-center mb-3 img-ctn" v-if="productImage">
              <img :src="productImage">
              <!-- v-if sits on the tooltip: Vuetify only lifts the activator out of the
                   display:none .v-tooltip wrapper when it exists at mount time -->
              <v-tooltip v-if="productForm.image" bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn class="delete-btn" icon
                         v-bind="attrs" v-on="{ ...on, click: removeProductPicture }">
                    <v-icon color="red">mdi-delete-outline</v-icon>
                  </v-btn>
                </template>
                <span class="black--text body-2">{{ $t('delete_image') }}</span>
              </v-tooltip>
            </div>
            <ImageCropper input-key="logo" @image-selected="onImageSelect" :gallery-only="true" :show-select-dialog="true"></ImageCropper>
          </div>
        </v-col>
        <v-col :cols="$vuetify.breakpoint.smAndDown ? 12 : 6" class="py-0 product-form pl-0">
          <v-form v-model="valid" ref="product-form">
            <v-row class="my-1 mx-0">
              <v-col :cols="12" class="pt-0 px-0">
                <v-text-field outlined dense small v-model="productForm.name" name="name" persistent-hint :label="$t('product_name')" :hint="$t('product_name_hint')" @focus="clearErrors('name')" :rules="rules.name" :error-messages="errorMessages.name" required></v-text-field>
              </v-col>
              <v-col :cols="12" class="pt-0 px-0">
                <v-text-field outlined dense small v-model="productForm.sku" name="sku" persistent-hint :label="$t('product_internal_name')" :hint="$t('product_sku_hint')" :rules="rules.sku" @focus="clearErrors('sku')" :error-messages="errorMessages.sku" required></v-text-field>
              </v-col>
              <v-col cols="12" class="pt-0 px-0">
                <v-textarea outlined dense small v-model="productForm.description" counter="500" :label="$t('description')" persistent-hint :hint="$t('description_hint')" :rules="rules.description"></v-textarea>
              </v-col>
            </v-row>
          </v-form>
        </v-col>
      </v-row>
    </div>
    <div class="actions-ctn">
      <v-btn :loading="loading" class="save-btn px-6" @click="save()" color="primary" raised>{{ $t('save') }}</v-btn>
    </div>
  </div>
</template>

<script>

import ImageCropper from '@/components/utils/ImageCropper'
import Utils from '@/utils/utils'
import { mapGetters, mapMutations } from 'vuex'

export default {
  name: 'ProductDetailsInfo',
  components: {
    ImageCropper
  },
  computed: {
    ...mapGetters(['selectedProduct']),
    productImage () {
      return this.productForm.image ? this.productForm.image : this.productForm.brand && this.productForm.brand.image ? this.productForm.brand.image : require('@/assets/product-placeholder.png')
    }
  },
  data () {
    return {
      valid: false,
      isFromGallery: false,
      loading: false,
      galleryImageKey: null,
      errorMessages: {
        name: [],
        sku: []
      },
      productForm: {},
      rules: {
        name: [
          v => !!v || this.$t('field_required'),
          v => (v || '').length <= 100 || this.$t('field_characters_limit', { count: 100 })

        ],
        sku: [
          v => !!v || this.$t('field_required'),
          v => (v || '').length <= 50 || this.$t('field_characters_limit', { count: 50 })
        ],
        description: [
          v => (v || '').length <= 500 || this.$t('field_characters_limit', { count: 500 })
        ]
      }
    }
  },
  watch: {
    productForm: {
      deep: true,
      handler (newVal) {
        this.setUnsavedChangesStatus(!Utils.deepCompare(newVal, this.selectedProduct))
      }
    }
  },
  methods: {
    ...mapMutations({
      setUnsavedChangesStatus: 'SET_UNSAVED_CHANGES_STATUS'
    }),

    onDiscardUnsavedChanges () {
      this.productForm = { ...this.selectedProduct }
    },

    clearErrors (key) {
      if (key) {
        this.errorMessages[key] = []
      } else {
        this.errorMessages = {
          name: [],
          sku: []
        }
      }
    },

    removeProductPicture () {
      this.productForm.image = null
      if (this.isFromGallery) this.isFromGallery = false
      if (this.galleryImageKey) this.galleryImageKey = null
    },

    onImageSelect (image, fromGallery = false) {
      if (fromGallery) {
        this.isFromGallery = fromGallery
        const selectedImage = image.file
        this.galleryImageKey = image.key
        this.productForm = Object.assign({}, this.productForm, { image: selectedImage })
      } else {
        this.productForm = Object.assign({}, this.productForm, { image: image })
      }
    },

    async save () {
      this.$refs['product-form'].validate()
      if (!this.valid) {
        return
      }
      this.loading = true
      try {
        if (this.isFromGallery && this.galleryImageKey) {
          this.productForm.gallery_image = this.galleryImageKey
        }
        const product = await this.$store.dispatch('updateProduct', { product: this.productForm })
        if (!this.isFromGallery && !this.productForm.image) {
          await this.$store.dispatch('deleteProductPicture', { product: product })
        }
        this.$store.dispatch('showSnackBar', { message: this.$t('saved_successfully') })
        this.setUnsavedChangesStatus(false)
        this.loading = false
        return Promise.resolve()
      } catch (err) {
        for (const key in err.data) {
          this.errorMessages[key] = err.data[key]
        }
        this.loading = false
      }
      return Promise.resolve()
    }
  },
  created () {
    if (this.selectedProduct && this.selectedProduct.id) {
      this.productForm = {
        ...this.selectedProduct
      }
    }
  },
  mounted () {
    window.addEventListener('discard-unsaved-changes', this.onDiscardUnsavedChanges)
  },
  beforeDestroy () {
    window.removeEventListener('discard-unsaved-changes', this.onDiscardUnsavedChanges)
  }
}

</script>

<style lang="sass" scoped>

.product-form
  max-width: 550px

.image-hint
  color: #000
  text-align: center
  font-feature-settings: 'liga' off, 'clig' off
  font-family: Inter
  font-size: 10px
  font-style: normal
  font-weight: 300
  line-height: 16px

.img-ctn
  position: relative
  width: 100%
  display: flex
  justify-content: center
  align-items: center
  img
    max-width: 250px
    display: block
    margin-left: auto
    margin-right: auto
  .delete-btn
    position: absolute
    top: 10px
    right: 10px

.product-info
  display: flex
  flex-direction: column
  height: 100%
  min-height: 0

.info-content
  flex: 1 1 auto
  overflow-y: auto
  padding-bottom: 8px
  min-height: 0

.actions-ctn
  position: sticky
  bottom: 0
  background: white
  padding: 16px 24px
  text-align: left
  border-top: 1px solid #eee
  z-index: 5

.save-btn
  margin: 0
  min-width: 140px

  @media (max-width: 960px)
    .actions-ctn
      position: fixed
      left: 0
      right: 0
      bottom: 0
      padding: 12px 16px
      z-index: 20
    .info-content
      padding-bottom: 80px
</style>
